/**
 * HitlSubmitter — schema-driven HITL response form, shared between ora and
 * the Outlook plugin.
 *
 * One canonical component for any HITL question whose payload includes a
 * JSON Schema. Renders the question prompt as a heading, the schema as
 * fields via the shared ``JsonSchemaForm``, and submits the **unwrapped**
 * form values to a parent-supplied ``onSubmit(values)`` callback.
 *
 * Why this exists: ora and the plugin previously each shipped their own
 * dispatchers (``HitlPanel``, ``SchemaHitlPanel``, ``HitlEventDispatcher``,
 * ``HitlResponseDrawer``) and several of them wrapped the user's response
 * in ``{ approved: true, corrections: formValue }`` before sending. That
 * wrapper is a legacy contract from the early HITL design; modern
 * schema-driven workflows like CNBC's ``confirm_decision`` read fields
 * from the answer dict directly (e.g. ``state["hitl_answer"]["decision"]``)
 * and break when the wrap puts the field one level deeper. The fix is to
 * use the same submitter from both surfaces and have it always pass the
 * raw form values through; transports stay app-specific (ora uses
 * Remix's ``useFetcher``, plugin uses ``orchestratorApi.resumeInstance``)
 * but the shape going across the wire is identical.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import JsonSchemaForm from "./JsonSchemaForm";
import type { ConfidenceLevel } from "./JsonSchemaForm";
import { getQuestionPrompt } from "../getQuestionPrompt";
import { renderInlineBold } from "../renderInlineBold";

export interface HitlSubmitterProps {
  /** The HITL question payload, as forwarded from the substrate. */
  hitlQuestion: Record<string, unknown> | null | undefined;
  /**
   * Called with the unwrapped form values. The parent wires this to its
   * transport (Remix fetcher in ora, ``orchestratorApi.resumeInstance``
   * in the plugin). The parent MUST submit ``values`` verbatim — no
   * ``{ approved, corrections }`` envelope. Workflows that need to
   * inspect specific fields read them directly from the answer dict
   * (e.g. ``state["hitl_answer"]["decision"]``).
   */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /** Optional cancel affordance. */
  onCancel?: () => void;
  /** Submit button label. Defaults to "Submit response". */
  submitLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Title above the form. Defaults to ``hitlQuestion.question`` or "Respond to continue". */
  title?: string;
  /** Plugin-only opt-in: per-field confidence chips for analyze_email prefill. */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  /** Plugin-only opt-in: per-field loading spinner for in-flight prefill. */
  fieldLoading?: Record<string, boolean>;
  /** Optional pre-filled values (e.g. analyze_email seeds the form). */
  initialValues?: Record<string, unknown>;
  /** Externally controlled submitting state for parents that prefer to manage it. */
  submittingOverride?: boolean;
  /** Optional content rendered between the question heading and the form fields (e.g. evidence preview). */
  children?: ReactNode;
}

/** Extract the JSON schema from a question payload, if present and well-shaped. */
function getSchema(
  hitlQuestion: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!hitlQuestion || typeof hitlQuestion !== "object") return null;
  const s = hitlQuestion.schema;
  if (s && typeof s === "object" && !Array.isArray(s)) {
    return s as Record<string, unknown>;
  }
  return null;
}

/**
 * Renders a schema-driven HITL form. Returns ``null`` when the question
 * doesn't carry a schema — callers handle non-schema HITLs (legacy
 * checkpoint dispatch, screenshot review deck navigation, etc.) themselves.
 */
export function HitlSubmitter({
  hitlQuestion,
  onSubmit,
  onCancel,
  submitLabel = "Submit response",
  cancelLabel = "Cancel",
  title,
  fieldConfidence,
  fieldLoading,
  initialValues,
  submittingOverride,
  children,
}: HitlSubmitterProps) {
  const [submitting, setSubmitting] = useState(false);
  // Reset the local submitting state whenever a new HITL question arrives.
  // Without this guard, rapid back-to-back questions (e.g. step 1 resume →
  // step 2 paused) can mount the new form with submitting=true still set
  // from the previous response, leaving pills disabled (gray) until the
  // user hovers or interacts. The reset is a no-op on first mount.
  useEffect(() => {
    setSubmitting(false);
  }, [hitlQuestion]);
  const schema = getSchema(hitlQuestion);
  if (!schema) return null;

  const heading = title ?? getQuestionPrompt(hitlQuestion);
  const effectiveSubmitting = submittingOverride ?? submitting;

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setSubmitting(true);
      try {
        await onSubmit(values);
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit],
  );

  // Re-mount the inner JsonSchemaForm whenever the question payload changes
  // so its uncontrolled internal state (typed values, pending pill clicks)
  // resets between questions. Without this, an MFA retry HITL shows the
  // AM's previous wrong code already typed in the input, and they have to
  // delete it manually before retyping. The memoized key changes only when
  // hitlQuestion changes, so ordinary parent re-renders don't blow away
  // in-progress typing.
  const formKey = useMemo(
    () => JSON.stringify(hitlQuestion ?? null),
    [hitlQuestion],
  );

  return (
    <div className="flex flex-col gap-3" data-testid="hitl-submitter">
      {/* whitespace-pre-wrap preserves \n\n paragraph breaks in pending_action. */}
      <h3 className="text-md font-semibold text-primary-gray-light-900 whitespace-pre-wrap">
        {renderInlineBold(heading)}
      </h3>
      {children}
      <JsonSchemaForm
        key={formKey}
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        submitting={effectiveSubmitting}
        submitLabel={submitLabel}
        cancelLabel={cancelLabel}
        fieldConfidence={fieldConfidence}
        fieldLoading={fieldLoading}
        submitOnPillSelect
      />
    </div>
  );
}

export default HitlSubmitter;
